import React, { useState } from "react";
import type {
  Breadcrumb,
  LayoutConfig,
  LayoutContextProps,
} from "../../types/layout";
import type { ChildContainerProps } from "../../types";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { isTestEnvironment } from "../../utils/environment";

export const LayoutContext = React.createContext({} as LayoutContextProps);

export const LayoutProvider = (props: ChildContainerProps) => {
  const [layoutConfig] = useState<LayoutConfig>({
    ripple: true,
    inputStyle: "outlined",
    menuMode: "static", // Only static mode used in CG version
    menuTheme: "darkgray",
    colorScheme: "light",
    theme: "blue",
    scale: 14,
  });

  const [layoutState, setLayoutState] = useState({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false, // still optionally used
    overlaySubmenuActive: false,
    rightMenuVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    searchBarActive: false,
    resetMenu: false,
    sidebarActive: false,
    anchored: false,
  });

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([
    { labels: ["Home"], to: "/" },
    { labels: ["Station List"], to: "/station-list" },
    { labels: ["Station Details"], to: "/station-details/:id" },
    { labels: ["Add Owner"], to: "/add-owner/:id" },
    { labels: ["Transactions"], to: "/transactions/:id" },
    { labels: ["Clearing"], to: "/clearing" },
    { labels: ["Clearing Details"], to: "/clearing-details" },
    { labels: ["Customers"], to: "/customers" },
    { labels: ["Update Customer"], to: "/customers/:id" },
    { labels: ["Default Prices"], to: "/default-prices" },
    { labels: ["Default Prices Edit"], to: "/default-prices/edit" },
  ]);

  const onMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDesktop()) {
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
      }));
      event.preventDefault();
    }
  };

  const hideOverlayMenu = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      staticMenuMobileActive: false,
    }));
  };

  const toggleSearch = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      searchBarActive: !prevLayoutState.searchBarActive,
    }));
  };

  const onSearchHide = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      searchBarActive: false,
    }));
  };

  const showRightSidebar = () => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      rightMenuVisible: true,
    }));
    hideOverlayMenu();
  };

  const isDesktop = () => {
    return window.innerWidth > 991;
  };

  const value: LayoutContextProps = {
    layoutConfig,
    setLayoutConfig: () => {}, // No theme switching in CG mode
    layoutState,
    setLayoutState,
    isSlim: () => false,
    isCompact: () => false,
    isHorizontal: () => false,
    isDesktop,
    onMenuToggle,
    toggleSearch,
    onSearchHide,
    showRightSidebar,
    breadcrumbs,
    setBreadcrumbs,
  };

  return (
    <HelmetProvider>
      <LayoutContext.Provider value={value}>
        <>
          <Helmet>
            <title>{process.env.NODE_ENV === 'development' ? '[DEV] Admin Dashboard' : isTestEnvironment ? '[TEST] Admin Dashboard' : 'cocharge Admin Dashboard'}</title>
            <meta charSet="UTF-8" />
            <meta
              name="viewport"
              content="initial-scale=1, width=device-width"
            />
            <link rel="icon" href={`/favicon.ico`} type="image/x-icon"></link>
          </Helmet>
          {props.children}
        </>
      </LayoutContext.Provider>
    </HelmetProvider>
  );
};
